import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  PLATFORM_ID,
  inject,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { BookingService } from '../../../core/api/booking.service';
import { Booking } from '../../../core/models/booking.model';
import { formatDateTime } from '../../../shared/utils/date.utils';

/**
 * Страница «Отчёт по бронированию»: плоский текст со всеми полями
 * booking-detail, который удобно скопировать и переслать в мессенджер.
 */
@Component({
  selector: 'app-booking-report',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
  templateUrl: './booking-report.component.html',
  styleUrl: './booking-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingReportComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly bookingService = inject(BookingService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly platformId = inject(PLATFORM_ID);

  readonly loading = signal(true);
  readonly booking = signal<Booking | null>(null);
  readonly reportText = signal('');
  readonly bookingId = signal<number>(0);
  readonly copyError = signal(false);

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      this.loading.set(false);
      return;
    }
    this.bookingId.set(id);
    this.bookingService.get(id).subscribe({
      next: (b) => {
        this.booking.set(b);
        this.reportText.set(this.buildReport(b));
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  copy(): void {
    const text = this.reportText();
    if (!text) {
      this.snackBar.open('Отчёт ещё не сформирован', 'ОК', { duration: 3000 });
      return;
    }
    if (!isPlatformBrowser(this.platformId)) {
      this.snackBar.open('Копирование недоступно в этой среде', 'ОК', { duration: 3000 });
      return;
    }
    this.copyToClipboard(text)
      .then(() => {
        this.copyError.set(false);
        this.snackBar.open('Скопировано', 'ОК', { duration: 2000 });
      })
      .catch(() => {
        this.copyError.set(true);
        this.snackBar.open('Ошибка копирования', 'ОК', { duration: 3000 });
      });
  }

  /**
   * Шеринг через системный Share Sheet (navigator.share).
   * На iPhone/в Telegram вставка многострочного текста уходит в Premium Rich Text,
   * поэтому для сохранения переносов строк лучше «Поделиться», а не копирование.
   */
  async share(): Promise<void> {
    const text = this.reportText();
    if (!text) {
      this.snackBar.open('Отчёт ещё не сформирован', 'ОК', { duration: 3000 });
      return;
    }
    if (!isPlatformBrowser(this.platformId)) {
      this.snackBar.open('Поделиться недоступно в этой среде', 'ОК', { duration: 3000 });
      return;
    }
    if (typeof navigator.share !== 'function') {
      this.snackBar.open('Поделиться недоступно на этом устройстве', 'ОК', { duration: 3000 });
      return;
    }
    try {
      await navigator.share({ text });
    } catch (error) {
      // Отмена пользователем (AbortError, в т.ч. старый WebKit) — молча.
      if (this.isAbortError(error)) {
        return;
      }
      this.snackBar.open('Не удалось поделиться', 'ОК', { duration: 3000 });
    }
  }

  private isAbortError(error: unknown): boolean {
    if (error instanceof DOMException) {
      return error.name === 'AbortError' || error.code === DOMException.ABORT_ERR;
    }
    const name = (error as { name?: unknown } | null)?.name;
    return name === 'AbortError' || name === 'NS_ERROR_DOM_ABORT_ERR';
  }

  /**
   * Сначала пробуем нативный Clipboard API (требует secure context),
   * в фоллбэке — скрытая `<textarea>` + `document.execCommand('copy')`,
   * который работает внутри Telegram Mini App и старых WebView.
   */
  private async copyToClipboard(text: string): Promise<void> {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch {
        // Падаем в фоллбэк, если браузер отказал (например, нет focus).
      }
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
    if (!ok) {
      throw new Error('execCommand copy failed');
    }
  }

  private fmt(value: string | null | undefined): string {
    return value ? formatDateTime(value) : '—';
  }

  private buildReport(b: Booking): string {
    const lines: string[] = [];

    lines.push(`Бронирование #${b.id}`);
    lines.push(`Статус: ${b.status_display || '—'}`);
    lines.push(`Клиент: ${b.client_name || '—'}`);
    lines.push(`Автомобиль: ${b.car_title || '—'}`);
    lines.push(`Госномер: ${b.license_plate || '—'}`);
    lines.push(`Дата начала: ${this.fmt(b.start_date)}`);
    lines.push(`Дата окончания: ${this.fmt(b.end_date)}`);

    if (b.deposit_amount) {
      lines.push(`Залог: ${b.deposit_amount} ₽`);
    }
    if (b.rental_amount) {
      lines.push(`Сумма аренды: ${b.rental_amount} ₽`);
    }

    if (b.notes) {
      lines.push('');
      lines.push('Заметки:');
      lines.push(b.notes);
    }

    lines.push('');
    lines.push(`Создано: ${this.fmt(b.created)}`);
    lines.push(`Изменено: ${this.fmt(b.modified)}`);

    return lines.join('\n');
  }
}
